// The WRITE half of the repo Issues tab (arc 101 slice 06): one tracked issue
// (`GET /v1/issues/:id`) plus the two forced overrides on it
// (`POST /v1/issues/:id/{escalate,handoff}` — `apps/api/src/routes/issues.ts`).
//
// This is not the dashboard triaging. The queue row, the webhook delivery and
// every audit row are minted server-side and the agent still does the work;
// what a human does here is pull a lever policy wouldn't have pulled, recorded
// as a human act (the session's `userId` rides into the audit chain).
//
// Both routes answer 202 with an HONEST outcome, never a bare ok: an escalation
// may be `deduped` onto an already-open incident, a handoff may come back
// `queued: false` (`no_webhook` / `already_pending`). Callers must render that
// difference — a forced action that fired nothing may never read like one that
// did. Field casing is the wire's, not ours: the route spreads the port's
// result, so `escalationId` / `sessionId` arrive camelCase next to `ok`.

import {
  type EscalationCategory,
  HARDCODED_ALWAYS,
  HARDCODED_ALWAYS_SET,
  USER_EXTENSIBLE_ALWAYS,
} from '@developerz/domain';
import { createQuery } from '@tanstack/solid-query';
import { activeAccountId } from './active-account';
import { apiFetch } from './api';
import type { RepoIssueRow } from './issues';
import { canOwnerWrite } from './roles';
import { SESSION_TOKEN } from './session';

export const ISSUE_DETAIL_QUERY_KEY = 'issue-detail';

/** Reason ceiling on both routes (`HandoffBodySchema` / `EscalateBodySchema`). */
export const ISSUE_REASON_MAX_CHARS = 2_000;
/** `webhook_ref` ceiling on the handoff route. */
export const WEBHOOK_REF_MAX_CHARS = 64;

/**
 * One tracked issue. Same projection as a list row (`IssueResource` serves both),
 * so the drawer can re-read the row it was opened with and show server truth
 * after a forced action. Idle until an issue is selected.
 */
export function useIssue(issueId: () => string | null, token: string | null = SESSION_TOKEN) {
  return createQuery<RepoIssueRow>(() => ({
    queryKey: [ISSUE_DETAIL_QUERY_KEY, issueId(), token, activeAccountId()],
    enabled: token !== null && issueId() !== null,
    queryFn: () =>
      apiFetch<RepoIssueRow>(`/v1/issues/${encodeURIComponent(issueId() ?? '')}`, token),
  }));
}

/**
 * The categories a human may force from here: the two "escalate always"
 * families, straight off `@developerz/domain` so the picker can't drift from
 * the schema the route validates against.
 *
 * The other families are deliberately NOT offered. `ASK_BEFORE_ACTING` names an
 * action the bot drafted, `SATISFYING` is a celebration, `OPERATIONAL` is a
 * machine fault (cost cap, provider failure) and `ROUTINE_DEFERRAL` is the
 * agent's own "hand this over" — hand-firing any of them would file an incident
 * describing something that never happened.
 */
export const FORCEABLE_CATEGORIES: readonly EscalationCategory[] = [
  ...HARDCODED_ALWAYS,
  ...USER_EXTENSIBLE_ALWAYS,
];

/**
 * True when the category pins to email-immediate inside the dispatcher — the
 * same set the route echoes back as `fires_immediately`. Surfaced BEFORE the
 * submit so nobody pages a human by accident.
 */
export const firesImmediately = (category: string): boolean =>
  HARDCODED_ALWAYS_SET.has(category as EscalationCategory);

/** `POST /v1/issues/:id/escalate` → 202. */
export type EscalateResult = {
  readonly ok: boolean;
  readonly escalationId: string;
  readonly fires_immediately: boolean;
  /** An open escalation for the same (issue, category) already existed (#500). */
  readonly deduped?: boolean;
};

/** `POST /v1/issues/:id/handoff` → 202. */
export type HandoffResult = {
  readonly ok: boolean;
  readonly sessionId: string | null;
  readonly queued: boolean;
  /** Why nothing fired, when `queued` is false. */
  readonly reason?: 'no_webhook' | 'already_pending';
};

/** Force an escalation — pull a human in, even where policy wouldn't have. */
export function forceEscalate(
  issueId: string,
  input: { category: string; reason: string },
  token: string | null = SESSION_TOKEN,
): Promise<EscalateResult> {
  return apiFetch<EscalateResult>(`/v1/issues/${encodeURIComponent(issueId)}/escalate`, token, {
    method: 'POST',
    body: JSON.stringify({ category: input.category, reason: input.reason }),
  });
}

/**
 * Force a handoff to the coding agent. `webhookRef` names ONE outbound webhook
 * by name (`resolveWebhookForDelivery`); `null` lets the account's
 * `issue.handoff` subscriber answer for it.
 */
export function forceHandoff(
  issueId: string,
  input: { reason: string; webhookRef: string | null },
  token: string | null = SESSION_TOKEN,
): Promise<HandoffResult> {
  return apiFetch<HandoffResult>(`/v1/issues/${encodeURIComponent(issueId)}/handoff`, token, {
    method: 'POST',
    body: JSON.stringify({
      reason: input.reason,
      ...(input.webhookRef ? { webhook_ref: input.webhookRef } : {}),
    }),
  });
}

/**
 * Whether to offer this caller the forced overrides. The server is the
 * authority (both routes are `write:issues`); the client mirrors it only to hide
 * controls from a caller who would plainly 403. The gate itself is
 * `canOwnerWrite` (`lib/roles.ts`), shared with the other owner-only writes.
 */
export const canForceIssueActions = canOwnerWrite;
